import ValeurEstimee from './ValeurEstimee';
import { useState, useRef, useLayoutEffect } from 'react';
import { STATUTS, ETATS_GENERAUX, DEFAUTS_PHYSIQUES } from '../utils/constants';
import { genreColor } from '../utils/genreColors';

const T = {
  orange:'var(--orange)', orangeD:'var(--orange-d)', ink:'var(--ink)', surface:'var(--surface)',
  border:'var(--border)', borderMd:'var(--border-md)', muted:'var(--muted)', subtle:'var(--subtle)',
  g1:'var(--surface2)', g2:'var(--border)', green:'#16A34A', red:'#DC2626',
  yellow:'#F5C842', blue:'#2563EB', purple:'#7C3AED',
};
const FONT    = "'Inter',-apple-system,sans-serif";

const getStatut = v => STATUTS.find(s=>s.value===v)||STATUTS[0];
const getEtat   = v => ETATS_GENERAUX.find(e=>e.value===v);

/* ── Carousel photos ─────────────────────────────────────── */
function PhotoCarousel({ photos }) {
  const [idx, setIdx] = useState(0);
  const [lightbox, setLightbox] = useState(false);

  if (!photos?.length) return (
    <div style={{height:'220px',background:'linear-gradient(145deg,var(--orange-pale),var(--surface2))',display:'flex',alignItems:'center',justifyContent:'center',flexDirection:'column',gap:'8px'}}>
      <span style={{fontSize:'3rem',opacity:.3}}>📚</span>
      <span style={{fontSize:'13px',color:T.subtle,fontFamily:FONT}}>Aucune photo</span>
    </div>
  );

  const cur = photos[idx];
  return (
    <>
      <div style={{position:'relative',background:'#000',overflow:'hidden',height:'260px',cursor:'zoom-in'}} onClick={()=>setLightbox(true)}>
        <img src={cur.url} alt=""
          style={{width:'100%',height:'100%',objectFit:'contain',display:'block',transition:'opacity .2s'}}/>

        {/* Prev / Next */}
        {photos.length>1 && (
          <>
            <button onClick={e=>{e.stopPropagation();setIdx(i=>(i-1+photos.length)%photos.length);}}
              style={arrowBtn('left')}>‹</button>
            <button onClick={e=>{e.stopPropagation();setIdx(i=>(i+1)%photos.length);}}
              style={arrowBtn('right')}>›</button>
            {/* Dots */}
            <div style={{position:'absolute',bottom:'10px',left:0,right:0,display:'flex',justifyContent:'center',gap:'5px'}}>
              {photos.map((_,i)=>(
                <div key={i} onClick={e=>{e.stopPropagation();setIdx(i);}}
                  style={{width:i===idx?'18px':'7px',height:'7px',borderRadius:'99px',background:i===idx?T.orange:'rgba(255,255,255,.6)',transition:'all .2s',cursor:'pointer'}}/>
              ))}
            </div>
            {/* Compteur */}
            <div style={{position:'absolute',top:'10px',right:'10px',background:'rgba(0,0,0,.55)',color:'#fff',borderRadius:'99px',padding:'3px 10px',fontSize:'11px',fontWeight:600,fontFamily:FONT}}>
              {idx+1} / {photos.length}
            </div>
          </>
        )}
        {/* Zoom hint */}
        <div style={{position:'absolute',bottom:'10px',right:'10px',background:'rgba(0,0,0,.55)',color:'rgba(255,255,255,.8)',borderRadius:'8px',padding:'4px 8px',fontSize:'11px',fontFamily:FONT}}>⊕</div>
      </div>

      {/* Thumbnails */}
      {photos.length>1 && (
        <div style={{display:'flex',gap:'6px',padding:'10px 16px',background:T.g1,overflowX:'auto',scrollbarWidth:'none'}}>
          {photos.map((p,i)=>(
            <div key={i} onClick={()=>setIdx(i)}
              style={{width:'48px',height:'48px',borderRadius:'6px',overflow:'hidden',flexShrink:0,cursor:'pointer',border:`2px solid ${i===idx?T.orange:'transparent'}`,transition:'border-color .15s',opacity:i===idx?1:.65}}>
              <img src={p.url} alt="" style={{width:'100%',height:'100%',objectFit:'cover'}}/>
            </div>
          ))}
        </div>
      )}

      {/* Lightbox */}
      {lightbox && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.92)',zIndex:2000,display:'flex',alignItems:'center',justifyContent:'center'}}
          onClick={()=>setLightbox(false)}>
          <img src={cur.url} alt="" style={{maxWidth:'95vw',maxHeight:'90vh',objectFit:'contain',borderRadius:'4px'}}
            onClick={e=>e.stopPropagation()}/>
          <button onClick={()=>setLightbox(false)}
            style={{position:'absolute',top:'16px',right:'16px',width:'36px',height:'36px',borderRadius:'50%',background:'rgba(255,255,255,.15)',border:'none',color:'#fff',fontSize:'20px',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center'}}>×</button>
          {photos.length>1 && <>
            <button onClick={e=>{e.stopPropagation();setIdx(i=>(i-1+photos.length)%photos.length);}} style={arrowBtn('left')}>‹</button>
            <button onClick={e=>{e.stopPropagation();setIdx(i=>(i+1)%photos.length);}} style={arrowBtn('right')}>›</button>
          </>}
        </div>
      )}
    </>
  );
}

const arrowBtn = side => ({
  position:'absolute', top:'50%', transform:'translateY(-50%)',
  [side]:'10px', width:'36px', height:'36px', borderRadius:'50%',
  background:'rgba(0,0,0,.5)', border:'none', color:'#fff',
  fontSize:'22px', cursor:'pointer', display:'flex', alignItems:'center',
  justifyContent:'center', backdropFilter:'blur(4px)', lineHeight:1,
  transition:'background .15s',
});

/* ── Ligne info ──────────────────────────────────────────── */
const Row = ({ label, value, accent }) => value ? (
  <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',gap:'12px',padding:'9px 0',borderBottom:`1px solid ${T.border}`}}>
    <span style={{fontSize:'12px',fontWeight:600,color:T.muted,fontFamily:FONT,flexShrink:0,paddingTop:'1px'}}>{label}</span>
    <span style={{fontSize:'13px',fontWeight:500,color:accent||T.ink,fontFamily:FONT,textAlign:'right',lineHeight:1.4}}>{value}</span>
  </div>
) : null;

/* ── Badge ───────────────────────────────────────────────── */
const Badge = ({label,bg,color,border}) => (
  <span style={{display:'inline-block',background:bg,color,border:`1px solid ${border||bg}`,borderRadius:'99px',padding:'3px 10px',fontSize:'12px',fontWeight:700,fontFamily:FONT}}>{label}</span>
);

/* ── Carte exemplaire (pour les doublons) ────────────────── */
function ExemplaireCard({ bd, onEdit, onDelete, number }) {
  const statut = getStatut(bd.statut);
  const etat   = getEtat(bd.etatGeneral);
  return (
    <div style={{background:T.g1,border:`1px solid ${T.border}`,borderRadius:'12px',padding:'12px 14px'}}>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'10px'}}>
        <div style={{fontSize:'12px',fontWeight:700,color:T.muted,fontFamily:FONT,textTransform:'uppercase',letterSpacing:'.05em'}}>
          Exemplaire {number}
        </div>
        <div style={{display:'flex',gap:'6px',alignItems:'center'}}>
          <Badge label={`${statut.emoji} ${statut.label}`} bg={statut.color+'18'} color={statut.color} border={statut.color+'33'}/>
        </div>
      </div>
      <div style={{display:'flex',flexDirection:'column',gap:'2px'}}>
        {bd.editeur && <div style={{fontSize:'13px',color:T.ink,fontFamily:FONT}}><span style={{color:T.muted,fontSize:'11px',fontWeight:600}}>Éditeur </span>{bd.editeur}</div>}
        {bd.dateParution && <div style={{fontSize:'13px',color:T.ink,fontFamily:FONT}}><span style={{color:T.muted,fontSize:'11px',fontWeight:600}}>Année </span>{bd.dateParution}</div>}
        {bd.collection && <div style={{fontSize:'13px',color:T.ink,fontFamily:FONT}}><span style={{color:T.muted,fontSize:'11px',fontWeight:600}}>Collection </span>{bd.collection}</div>}
        {etat && <div style={{fontSize:'13px',fontFamily:FONT}}><span style={{color:T.muted,fontSize:'11px',fontWeight:600}}>État </span><span style={{color:etat.color,fontWeight:700}}>{etat.label}</span></div>}
        {bd.prixAchat && <div style={{fontSize:'13px',color:T.ink,fontFamily:FONT}}><span style={{color:T.muted,fontSize:'11px',fontWeight:600}}>Prix achat </span>{parseFloat(bd.prixAchat).toFixed(2)} €</div>}
        {bd.prixVente && bd.statut==='vendre' && <div style={{fontSize:'13px',fontWeight:700,color:T.red,fontFamily:FONT}}>🏷️ À vendre : {parseFloat(bd.prixVente).toFixed(2)} €</div>}
        {bd.lieu && <div style={{fontSize:'13px',color:T.ink,fontFamily:FONT}}><span style={{color:T.muted,fontSize:'11px',fontWeight:600}}>Lieu </span>{bd.lieu}{bd.emplacement?' — '+bd.emplacement:''}</div>}
        {bd.defauts?.length>0 && (
          <div style={{marginTop:'6px',display:'flex',flexWrap:'wrap',gap:'4px'}}>
            {bd.defauts.map(d=><span key={d} style={{fontSize:'11px',background:'#FEF2F2',color:T.red,border:'1px solid #FECACA',borderRadius:'4px',padding:'2px 6px',fontFamily:FONT}}>{d}</span>)}
          </div>
        )}
        {bd.notes && <div style={{marginTop:'6px',fontSize:'12px',color:T.muted,fontFamily:FONT,lineHeight:1.5,fontStyle:'italic'}}>"{bd.notes}"</div>}
      </div>
      <div style={{display:'flex',gap:'8px',marginTop:'12px'}}>
        <button onClick={()=>onEdit(bd)} style={{flex:1,padding:'8px',border:`1px solid ${T.border}`,borderRadius:'8px',background:T.surface,fontFamily:FONT,fontSize:'13px',fontWeight:600,color:T.muted,cursor:'pointer',transition:'all .15s'}}
          onMouseOver={e=>{e.currentTarget.style.borderColor=T.orange;e.currentTarget.style.color=T.orange;}}
          onMouseOut={e=>{e.currentTarget.style.borderColor=T.border;e.currentTarget.style.color=T.muted;}}>
          ✏️ Modifier
        </button>
        <button onClick={()=>onDelete(bd)} style={{padding:'8px 12px',border:`1px solid #FECACA`,borderRadius:'8px',background:'#FEF2F2',fontFamily:FONT,fontSize:'13px',fontWeight:600,color:T.red,cursor:'pointer'}}
          onMouseOver={e=>e.currentTarget.style.background='#FEE2E2'}
          onMouseOut={e=>e.currentTarget.style.background='#FEF2F2'}>
          🗑
        </button>
      </div>
    </div>
  );
}

/* ══ BDDetail principal ═══════════════════════════════════════════════════════ */
export default function BDDetail({ bds, onClose, onEdit, onDelete, onAddExemplaire, origin }) {
  // bds = tableau de 1 ou plusieurs exemplaires du même album (même serie+numero+titre)
  // On prend le premier comme "référence" pour les infos communes
  const ref = bds[0];
  const hasMultiple = bds.length > 1;
  const statut  = getStatut(ref.statut);
  const etat    = getEtat(ref.etatGeneral);

  const sheetRef = useRef(null);

  // Effet "zoom depuis la couverture" : la fiche s'ouvre en grandissant depuis
  // l'endroit exact où l'album a été cliqué (origin = rect de la carte/ligne).
  useLayoutEffect(() => {
    const el = sheetRef.current;
    if (!el || !origin) return;
    const rect = el.getBoundingClientRect();
    const ox = (origin.left + origin.width / 2) - rect.left;
    const oy = (origin.top + origin.height / 2) - rect.top;
    el.style.transformOrigin = `${ox}px ${oy}px`;
    el.style.transform = 'scale(.05)';
    el.style.opacity = '0';
    el.getBoundingClientRect(); // force le navigateur à figer cet état avant la transition
    requestAnimationFrame(() => {
      el.style.transition = 'transform .38s cubic-bezier(.22,1,.36,1), opacity .28s ease';
      el.style.transform = 'scale(1)';
      el.style.opacity = '1';
    });
  }, [origin]);

  const handleClose = () => {
    const el = sheetRef.current;
    if (el && origin) {
      el.style.transition = 'transform .26s cubic-bezier(.4,0,1,1), opacity .2s ease';
      el.style.transform = 'scale(.05)';
      el.style.opacity = '0';
      setTimeout(onClose, 240);
    } else {
      onClose();
    }
  };

  // Rassembler toutes les photos de tous les exemplaires
  const allPhotos = bds.flatMap(b => b.photos||[]);

  // Infos communes (on affiche celles du premier exemplaire)
  const stars = ref.note>0 ? '★'.repeat(ref.note)+'☆'.repeat(5-ref.note) : null;

  // Trouver la catégorie d'un défaut
  const getDefautCat = (d) => {
    for (const [, cat] of Object.entries(DEFAUTS_PHYSIQUES)) {
      if (cat.items.includes(d)) return cat.label;
    }
    return 'Autre';
  };

  return (
    <div
      style={{position:'fixed',inset:0,background:'rgba(28,22,18,.65)',backdropFilter:'blur(8px)',WebkitBackdropFilter:'blur(8px)',display:'flex',alignItems:'flex-end',justifyContent:'center',zIndex:1000}}
      onClick={e=>e.target===e.currentTarget&&handleClose()}>

      <div ref={sheetRef} style={{
        background:T.surface, borderRadius:'22px 22px 0 0',
        border:`1px solid ${T.border}`, borderBottom:'none',
        width:'100%', maxWidth:'600px',
        maxHeight:'min(95vh, 95dvh)',
        overflowY:'auto', overflowX:'hidden', fontFamily:FONT,
        boxShadow:'0 -16px 48px rgba(0,0,0,.2)',
        animation: origin ? 'none' : 'slideUp .35s cubic-bezier(.22,1,.36,1)',
      }}>

        {/* ── Header sticky ── */}
        <div style={{position:'sticky',top:0,zIndex:10,background:'rgba(255,255,255,.95)',backdropFilter:'blur(10px)',WebkitBackdropFilter:'blur(10px)',borderBottom:`1px solid ${T.border}`,padding:'.9rem 1.25rem',display:'flex',alignItems:'center',justifyContent:'space-between',borderRadius:'22px 22px 0 0'}}>
          <div style={{minWidth:0,flex:1,marginRight:'12px'}}>
            {ref.serie && <div style={{fontSize:'11px',fontWeight:700,color:T.orange,textTransform:'uppercase',letterSpacing:'.07em',marginBottom:'1px'}}>{ref.serie}{ref.numero?` · T.${ref.numero}`:''}</div>}
            <div style={{fontSize:'16px',fontWeight:700,color:T.ink,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{ref.titre||'(Sans titre)'}</div>
          </div>
          <button onClick={handleClose} style={{width:'32px',height:'32px',borderRadius:'50%',border:`1px solid ${T.border}`,background:T.g1,cursor:'pointer',fontSize:'18px',color:T.muted,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>×</button>
        </div>

        {/* ── Photos ── */}
        <PhotoCarousel photos={allPhotos}/>

        {/* ── Contenu ── */}
        <div style={{padding:'1.25rem'}}>

          {/* Badges statut + note + état */}
          <div style={{display:'flex',flexWrap:'wrap',gap:'8px',marginBottom:'1.25rem'}}>
            {!hasMultiple && <Badge label={`${statut.emoji} ${statut.label}`} bg={statut.color+'18'} color={statut.color} border={statut.color+'33'}/>}
            {stars && <Badge label={stars} bg={T.yellow+'22'} color='#92400E' border={T.yellow+'44'}/>}
            {etat && !hasMultiple && <Badge label={etat.label} bg={etat.color+'18'} color={etat.color} border={etat.color+'33'}/>}
            {ref.eo       && <Badge label="1ère édition" bg='#FFFBEB' color='#92400E' border='#FDE68A'/>}
            {ref.dedicace && <Badge label="Dédicacé" bg='#F0FDF4' color='#15803D' border='#BBF7D0'/>}
            {ref.numerote && <Badge label="Numéroté" bg='#F5F3FF' color='#7C3AED' border='#DDD6FE'/>}
            {ref.lu && !hasMultiple && <Badge label="✓ Lu" bg='#EFF6FF' color='#1D4ED8' border='#BFDBFE'/>}
            {hasMultiple && <Badge label={`${bds.length} exemplaires`} bg='var(--orange-pale)' color={T.orange} border='#FDBA74'/>}
          </div>

          {/* ── CAS 1 : exemplaire unique ── */}
          {!hasMultiple && (
            <>
              {/* Infos auteurs/édition */}
              <Section title="Auteurs & Édition">
                <Row label="Scénariste"   value={ref.scenariste}/>
                <Row label="Dessinateur"  value={ref.dessinateur}/>
                <Row label="Coloriste"    value={ref.coloriste}/>
                <Row label="Auteur"       value={ref.auteur}/>
                <Row label="Éditeur"      value={ref.editeur}/>
                <Row label="Collection"   value={ref.collection}/>
                <Row label="Genre" value={ref.genre ? <span style={{display:'inline-flex',alignItems:'center',gap:'6px'}}><span style={{width:'8px',height:'8px',borderRadius:'50%',background:genreColor(ref.genre),flexShrink:0}}/>{ref.genre}</span> : null}/>
                <Row label="Format"       value={ref.format}/>
                <Row label="Langue"       value={ref.langue!=='Français'?ref.langue:null}/>
                <Row label="Année"        value={ref.dateParution}/>
                <Row label="Nb de pages"  value={ref.nbPages ? ref.nbPages+' p.' : null}/>
                <Row label="ISBN"         value={ref.isbn} accent={T.muted}/>
              </Section>

              {/* État & défauts */}
              {(ref.etatGeneral || ref.defauts?.length>0) && (
                <Section title="État physique">
                  {etat && <Row label="État général" value={etat.label} accent={etat.color}/>}
                  {ref.defauts?.length>0 && (
                    <div style={{paddingTop:'8px'}}>
                      <div style={{fontSize:'12px',fontWeight:600,color:T.muted,marginBottom:'8px'}}>Défauts constatés ({ref.defauts.length})</div>
                      <div style={{display:'flex',flexDirection:'column',gap:'6px'}}>
                        {Object.entries(
                          ref.defauts.reduce((acc,d)=>{const k=getDefautCat(d);if(!acc[k])acc[k]=[];acc[k].push(d);return acc;},{})
                        ).map(([cat,items])=>(
                          <div key={cat}>
                            <div style={{fontSize:'11px',fontWeight:700,color:T.orange,textTransform:'uppercase',letterSpacing:'.06em',marginBottom:'4px'}}>{cat}</div>
                            <div style={{display:'flex',flexWrap:'wrap',gap:'4px'}}>
                              {items.map(d=><span key={d} style={{fontSize:'12px',background:'#FEF2F2',color:T.red,border:'1px solid #FECACA',borderRadius:'6px',padding:'3px 8px',fontFamily:FONT}}>{d}</span>)}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </Section>
              )}

              {/* Valeur */}
              {(ref.prixAchat||ref.prixVente||ref.dateAchat||ref.lieuAchat) && (
                <Section title="Valeur & Acquisition">
                  <Row label="Prix d'achat"   value={ref.prixAchat?`${parseFloat(ref.prixAchat).toFixed(2)} €`:null}/>
                  {ref.statut==='vendre' && <Row label="Prix de revente" value={ref.prixVente?`${parseFloat(ref.prixVente).toFixed(2)} €`:null} accent={T.red}/>}
                  <Row label="Date d'achat"   value={ref.dateAchat ? new Date(ref.dateAchat).toLocaleDateString('fr-FR',{day:'numeric',month:'long',year:'numeric'}) : null}/>
                  <Row label="Acheté chez"    value={ref.lieuAchat}/>
                  <Row label="Date de lecture" value={ref.dateLecture ? new Date(ref.dateLecture).toLocaleDateString('fr-FR',{day:'numeric',month:'long',year:'numeric'}) : null}/>
                </Section>
              )}

              {/* Localisation */}
              {(ref.lieu||ref.emplacement) && (
                <Section title="Localisation">
                  <Row label="Lieu"       value={ref.lieu}/>
                  <Row label="Emplacement" value={ref.emplacement}/>
                </Section>
              )}

              {/* Notes */}
              {ref.notes && (
                <Section title="Notes personnelles">
                  <div style={{fontSize:'14px',color:T.ink,lineHeight:1.6,fontStyle:'italic',padding:'4px 0'}}>"{ref.notes}"</div>
                </Section>
              )}
            </>
          )}

          {/* ── CAS 2 : plusieurs exemplaires ── */}
          {hasMultiple && (
            <>
              {/* Infos communes (auteurs, qui ne changent pas) */}
              <Section title="Informations communes">
                <Row label="Scénariste"  value={ref.scenariste}/>
                <Row label="Dessinateur" value={ref.dessinateur}/>
                <Row label="Coloriste"   value={ref.coloriste}/>
                <Row label="Auteur"      value={ref.auteur}/>
                <Row label="Genre" value={ref.genre ? <span style={{display:'inline-flex',alignItems:'center',gap:'6px'}}><span style={{width:'8px',height:'8px',borderRadius:'50%',background:genreColor(ref.genre),flexShrink:0}}/>{ref.genre}</span> : null}/>
                <Row label="Format"      value={ref.format}/>
              </Section>

              <Section title={`Tes ${bds.length} exemplaires`} subtitle="Clique sur Modifier pour éditer un exemplaire spécifique">
                <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
                  {bds.map((b,i)=>(
                    <ExemplaireCard key={b.id} bd={b} number={i+1} onEdit={onEdit} onDelete={onDelete}/>
                  ))}
                </div>
              </Section>
            </>
          )}

          {/* ── Estimation de valeur ── */}
          {!hasMultiple && <ValeurEstimee bd={ref}/>}

          {/* ── Actions bas de fiche ── */}
          <div style={{display:'flex',gap:'10px',marginTop:'1rem'}}>
            {hasMultiple ? (
              <button onClick={onAddExemplaire} style={btnStyle(T.orange)}>
                + Ajouter un exemplaire
              </button>
            ) : (
              <>
                <button onClick={()=>onEdit(ref)} style={btnStyle(T.orange)}>✏️ Modifier</button>
                <button onClick={()=>onAddExemplaire(ref)} style={{...btnStyle(T.muted),flex:'0 0 auto',background:T.g1,boxShadow:'none',border:`1px solid ${T.border}`,color:T.muted}}>
                  + Dupliquer
                </button>
                <button onClick={()=>onDelete(ref)} style={{padding:'11px 14px',border:`1px solid #FECACA`,borderRadius:'10px',background:'#FEF2F2',fontFamily:FONT,fontSize:'14px',fontWeight:600,color:T.red,cursor:'pointer',flexShrink:0}}>
                  🗑
                </button>
              </>
            )}
          </div>
        </div>

        <style>{`
          @keyframes slideUp{from{opacity:0;transform:translateY(50px)}to{opacity:1;transform:translateY(0)}}
        `}</style>
      </div>
    </div>
  );
}

/* ── Micro-composants ─────────────────────────────────────── */
const Section = ({title,subtitle,children}) => (
  <div style={{marginBottom:'1.25rem'}}>
    <div style={{display:'flex',alignItems:'baseline',gap:'8px',marginBottom:'8px'}}>
      <div style={{fontSize:'11px',fontWeight:700,color:T.orange,textTransform:'uppercase',letterSpacing:'.07em',fontFamily:FONT}}>{title}</div>
      {subtitle && <div style={{fontSize:'11px',color:T.subtle,fontFamily:FONT}}>{subtitle}</div>}
    </div>
    {children}
  </div>
);

const btnStyle = (color) => ({
  flex:1, padding:'12px', border:'none', borderRadius:'10px',
  background:color, color:'#fff', fontFamily:FONT,
  fontSize:'14px', fontWeight:700, cursor:'pointer',
  boxShadow:`0 3px 12px ${color}44`,
  transition:'transform .15s, box-shadow .15s',
});
